import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import { LabClient } from 'models/client-directory';
import { Order } from 'models/order';
import { Organization } from 'models/organization';
import { disableOrderDeliveryDates } from 'utils/disable-order-delivery-dates';
import { getDeliveryAdvanceWorkingDate } from 'utils/internal-delivery-advance-working-date';

/** How far the pre-fill looks for an open day at the subcontractor around the deadline. */
const SEARCH_DAYS = 14;

/** True when the lab has opening hours set up but none of the days is open. */
export const hasNoOpeningDays = (lab?: Organization) => {
    const openingHours = lab?.openingHours ?? [];
    return openingHours.length > 0 && openingHours.every((day) => !day.active);
};

/**
 * The open day at the subcontractor closest to the deadline: the deadline itself, else the
 * nearest earlier day (not before today), else the nearest later one.
 */
export const nearestSelectableDate = (deadline: Date, supplier: Organization): Date | null => {
    const today = moment().startOf('day');
    const start = moment.max(moment(deadline).startOf('day'), today);
    const isSelectable = (day: moment.Moment) => !disableOrderDeliveryDates(day.toDate(), supplier);

    for (let day = start.clone(); day.isSameOrAfter(today) && start.diff(day, 'days') <= SEARCH_DAYS; ) {
        if (isSelectable(day)) return day.toDate();
        day = day.subtract(1, 'day');
    }
    for (let offset = 1; offset <= SEARCH_DAYS; offset++) {
        const day = start.clone().add(offset, 'days');
        if (isSelectable(day)) return day.toDate();
    }
    return null;
};

/** The parent's deadline for external users, or the default from the clinic's client settings. */
const externalDeadlineOf = (parentOrder: Order, labClient?: LabClient): Date | null => {
    const deliveryDetails = parentOrder.deliveryDetails;
    if (deliveryDetails?.externalDeliveryDate) return new Date(deliveryDetails.externalDeliveryDate);
    const daysAdvance = Number(labClient?.externalDeliveryDaysAdvance) || 0;
    if (!daysAdvance || !deliveryDetails?.date) return null;
    return getDeliveryAdvanceWorkingDate(new Date(deliveryDetails.date), daysAdvance);
};

/**
 * DD-3689: pre-fill the sub-order's delivery date once, from the deadline for external users
 * and sub-contractors. Runs only while the date is still empty, so a date the lab picked or
 * cleared is never overwritten.
 */
export const useDeliveryDatePrefill = () => {
    const { parentOrder, subcontract, updateSubcontract } = useSubcontractOrder();
    const { showBoundary } = useDenthubErrorBoundary();
    const { supplier } = subcontract;
    const needsPrefill = Boolean(supplier) && !subcontract.isDeliveryDatePrefilled && !subcontract.deliveryDate;
    const destinationId = parentOrder.deliveryDetails?.destination?._id;
    const needsClientSettings =
        needsPrefill && !parentOrder.deliveryDetails?.externalDeliveryDate && Boolean(destinationId);

    const { data, loading } = useQuery<{ labClientById: LabClient }>(getLabClientByIdTemplate, {
        variables: { customerId: destinationId },
        skip: !needsClientSettings,
        onError: showBoundary,
    });

    useEffect(() => {
        if (!needsPrefill || !supplier || loading) return;
        const deadline = externalDeadlineOf(parentOrder, data?.labClientById);
        const deliveryDate = deadline ? nearestSelectableDate(deadline, supplier) : null;
        updateSubcontract({
            isDeliveryDatePrefilled: true,
            ...(deliveryDate ? { deliveryDate, isDeliveryDateFromDeadline: true } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per prefill; updateSubcontract is not memoised
    }, [needsPrefill, supplier, loading, data]);
};
